import React, {
  createContext,
  useContext,
  useState,
  Dispatch,
  SetStateAction,
  FC,
  useEffect,
  useMemo,
} from 'react';
import SemItineraryProvider from './SemItineraryProvider';
import {
  Money,
  SemItinerarySelectionMutation,
  SemItinerarySelectionMutationVariables,
} from 'generated/graphql';
import {
  UseMutationResult,
  useMutation,
} from '@edreamsodigeo/frontend-api-client';
import { SEM_SELECT_ITINERARY } from 'clientState/providers/operations/semSelectItinerary';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import {
  useItineraryFeeTypeIdSelection,
  ItineraryFeeTypeId,
} from '../../ItinerarySelectionProvider/ItineraryFeeTypeProvider';
import itineraryResolver from '../../../resolvers/itinerary';
import {
  useFrontendApiBlockingError,
  useTracking,
} from '@edreamsodigeo/frontend-core';
import useSemItineraryTracking from './useSemItineraryTracking';
import { SEARCH_ID_DIMENSION } from 'clientState/hooks/useSetSearchGACustomDimensions/config';
import { useSemSelectTrackErrors } from './hooks';

type SemFee = {
  price: number;
  type: string;
};

const getFee = (semItinerary, feeTypeId): SemFee =>
  semItinerary.fees.find(({ type }) => type.id === feeTypeId);

const getSelectedItinerary = (semItinerary, feeTypeId) => {
  if (!feeTypeId || !semItinerary) {
    return semItinerary;
  }
  const fee = getFee(semItinerary, feeTypeId);
  return { ...semItinerary, price: fee?.price, feeType: fee?.type };
};

const getAccuracyTracking = (
  feeType: string | null,
  initialPrice?: number,
  newPrice?: number
): string => {
  if (!initialPrice || !newPrice) return '';
  const isPrime = feeType === 'MEMBER_PRICE_POLICY_DISCOUNTED' ? 1 : 0;
  return `inspirational_price_diff_prime:${isPrime}_ip:${initialPrice}_np:${newPrice}`;
};

export type SemItineraryInfo = {
  semItinerary: object;
  semItineraryPosition: number;
};

export type SemTrackingInfo = {
  returningUserDeviceId: string;
};

export type ResolvedItineraryInterface = {
  price?: Money;
};

const SemItinerarySelectionContext = createContext<
  Dispatch<SetStateAction<SemItineraryInfo>>
>(() => {});

const SemSearchIdSelectionContext = createContext<
  Dispatch<SetStateAction<number>>
>(() => {});

const SemTrackingInfoSelectionContext = createContext<
  Dispatch<SetStateAction<SemTrackingInfo>>
>(() => {});

const SemSelectItineraryMutationContext = createContext<UseMutationResult<
  SemItinerarySelectionMutation,
  SemItinerarySelectionMutationVariables
> | null>(null);

const SemItinerarySelectionProvider: FC = ({ children }) => {
  const [{ semItinerary, semItineraryPosition }, setSemItineraryInfo] =
    useState<{
      semItinerary: ResolvedItineraryInterface;
      semItineraryPosition: number;
    }>({ semItinerary: {}, semItineraryPosition: 0 });
  const [semSearchId, setSemSearchId] = useState(0);
  const { setCustomDimension } = useTracking();
  const [semTrackingInfo, setSemTrackingInfo] = useState({
    returningUserDeviceId: '',
  });
  const [feeType] = useFeeType();
  const selectItineraryFeeTypeId = useItineraryFeeTypeIdSelection();
  const { sendTracking } = useSemItineraryTracking();
  const { trackEvent } = useTracking();
  const mutationResult = useMutation<
    SemItinerarySelectionMutation,
    SemItinerarySelectionMutationVariables
  >(SEM_SELECT_ITINERARY);
  const { data } = mutationResult;
  const selectedItinerary = useMemo(() => {
    return getSelectedItinerary(
      data?.semItinerarySelection?.itineraries[0],
      feeType?.id
    );
  }, [data, feeType]);
  const resolvedItinerary = useMemo(() => {
    return selectedItinerary
      ? itineraryResolver(selectedItinerary, semItineraryPosition)
      : null;
  }, [selectedItinerary, semItineraryPosition]);
  // We need to pass down the resolved itinerary, but with the previous amount
  // so the repricing is not shown in the summary
  const mangledSemItinerary = !resolvedItinerary
    ? semItinerary
    : { ...resolvedItinerary, price: semItinerary.price };

  useFrontendApiBlockingError(mutationResult);
  useSemSelectTrackErrors(mutationResult);

  useEffect(() => {
    if (data && resolvedItinerary) {
      setCustomDimension(
        SEARCH_ID_DIMENSION,
        data.semItinerarySelection.searchId.toString()
      );
      setSemSearchId(data.semItinerarySelection.searchId);

      sendTracking(selectedItinerary, semItineraryPosition);
      selectItineraryFeeTypeId(feeType?.id as ItineraryFeeTypeId);
      trackEvent({
        category: 'flights_sum_inspirational',
        action: 'accuracy_prices',
        label: getAccuracyTracking(
          feeType?.id || null,
          semItinerary?.price?.amount,
          selectedItinerary?.price?.amount
        ),
      });
    }
  }, [
    data,
    feeType,
    resolvedItinerary,
    selectedItinerary,
    semItinerary,
    semItineraryPosition,
    sendTracking,
    setCustomDimension,
    selectItineraryFeeTypeId,
    trackEvent,
  ]);

  return (
    <SemItinerarySelectionContext.Provider value={setSemItineraryInfo}>
      <SemSearchIdSelectionContext.Provider value={setSemSearchId}>
        <SemTrackingInfoSelectionContext.Provider value={setSemTrackingInfo}>
          <SemSelectItineraryMutationContext.Provider value={mutationResult}>
            <SemItineraryProvider
              semItinerary={mangledSemItinerary}
              semItineraryPosition={semItineraryPosition}
              semSearchId={semSearchId}
              semTrackingInfo={semTrackingInfo}
            >
              {children}
            </SemItineraryProvider>
          </SemSelectItineraryMutationContext.Provider>
        </SemTrackingInfoSelectionContext.Provider>
      </SemSearchIdSelectionContext.Provider>
    </SemItinerarySelectionContext.Provider>
  );
};

export const useSemItineraryMutation = (): UseMutationResult<
  SemItinerarySelectionMutation,
  SemItinerarySelectionMutationVariables
> | null => useContext(SemSelectItineraryMutationContext);

export const useSemItinerarySelection = (): Dispatch<
  SetStateAction<SemItineraryInfo>
> => useContext(SemItinerarySelectionContext);

export const useSemSearchIdSelection = (): Dispatch<SetStateAction<number>> =>
  useContext(SemSearchIdSelectionContext);

export const useSemTrackingInfoSelection = (): Dispatch<
  SetStateAction<SemTrackingInfo>
> => useContext(SemTrackingInfoSelectionContext);

export default SemItinerarySelectionProvider;
